/**
 * Asset references: which project files a field accepts, how a file path is
 * stored in the document (`model:` / `gltf:` mesh-source prefixes), readable
 * type names, and the drag payload shared by the Assets panel, AssetField,
 * the scene view and the Hierarchy. Pure, so the rules are unit-tested.
 */

/** What an asset field accepts (the `path` field type of component forms). */
export interface AssetSpec {
  /** Accepted file endings (`.material.json`, `.png`, ...). */
  suffix: string | readonly string[];
  /** Prefix stored before a file path, per matching suffix (`{ '.glb': 'gltf:' }`). */
  prefixes?: Readonly<Record<string, string>>;
  /** Values that are not project files (`primitive:cube`), offered in the picker. */
  builtins?: readonly string[];
  /** Type name for the picker title and "None (…)"; derived from the suffix when absent. */
  label?: string;
}

export const MESH_SOURCE_SPEC: AssetSpec = {
  suffix: ['.model.json', '.glb', '.gltf'],
  prefixes: { '.model.json': 'model:', '.glb': 'gltf:', '.gltf': 'gltf:' },
  builtins: ['primitive:cube', 'primitive:sphere', 'primitive:capsule', 'primitive:cylinder', 'primitive:cone', 'primitive:plane', 'primitive:torus'],
  label: 'Mesh',
};

export const IMAGE_SUFFIXES = ['.png', '.jpg', '.jpeg', '.webp', '.pixel.json'] as const;
export const AUDIO_SUFFIXES = ['.ogg', '.wav', '.mp3', '.flac'] as const;
export const MODEL_SUFFIXES = ['.model.json', '.glb', '.gltf'] as const;

/** Readable names of the asset types fields ask for, by file ending. */
const TYPE_NAMES: [string, string][] = [
  ['.material.json', 'Material'],
  ['.physicsmat.json', 'Physics Material'],
  ['.prefab.json', 'Prefab'],
  ['.scene.json', 'Scene'],
  ['.model.json', 'Model'],
  ['.controller.json', 'Animator Controller'],
  ['.mixer.json', 'Audio Mixer'],
  ['.ui.json', 'UI Document'],
  ['.uss.json', 'Style Sheet'],
  ['.sound.json', 'Sound'],
  ['.video.json', 'Video Storyboard'],
  ['.pixel.json', 'Pixel Art'],
  ['.sprite.json', 'Sprite Sheet'],
  ['.terrain.json', 'Terrain Data'],
  ['.procgen.json', 'Procedural generator'],
  ['.rhai', 'Script'],
  ['.wgsl', 'Shader'],
  ['.glb', 'Model'],
  ['.gltf', 'Model'],
  ['.fbx', 'Model Source (FBX)'],
  ['.obj', 'Model Source (OBJ)'],
  ['.mtl', 'OBJ Materials'],
  ['.usd', 'Model Source (USD)'],
  ['.usda', 'Model Source (USD)'],
  ['.usdc', 'Model Source (USD)'],
  ['.usdz', 'Model Source (USDZ)'],
  ['.abc', 'Model Source (Alembic)'],
  ['.png', 'Texture'],
  ['.jpg', 'Texture'],
  ['.jpeg', 'Texture'],
  ['.webp', 'Texture'],
  ['.ogg', 'Audio Clip'],
  ['.wav', 'Audio Clip'],
  ['.mp3', 'Audio Clip'],
  ['.flac', 'Audio Clip'],
  ['.ttf', 'Font'],
  ['.otf', 'Font'],
];

export function suffixList(spec: Pick<AssetSpec, 'suffix'>): string[] {
  return typeof spec.suffix === 'string' ? [spec.suffix] : [...spec.suffix];
}

/** The accepted ending `path` has, if any (longest first, case-insensitive). */
export function matchingSuffix(spec: Pick<AssetSpec, 'suffix'>, path: string): string | null {
  const lower = path.toLowerCase();
  const found = suffixList(spec)
    .filter((s) => lower.endsWith(s.toLowerCase()))
    .sort((a, b) => b.length - a.length);
  return found[0] ?? null;
}

/** True when a project file can be assigned to a field with this spec. */
export function acceptsPath(spec: AssetSpec, path: string): boolean {
  return matchingSuffix(spec, path) !== null;
}

/** The value stored in the document for a project file. */
export function toAssetValue(spec: AssetSpec, path: string): string {
  const suffix = matchingSuffix(spec, path);
  const prefix = suffix ? spec.prefixes?.[suffix] : undefined;
  return `${prefix ?? ''}${path}`;
}

/**
 * Splits a stored value into the project file it points at (prefix and
 * `#fragment` removed) or a builtin (`primitive:cube`).
 */
export function parseAssetValue(spec: AssetSpec, value: unknown): { path: string | null; builtin: string | null; raw: string } {
  const raw = typeof value === 'string' ? value : '';
  if (!raw) return { path: null, builtin: null, raw };
  const prefixes = [...new Set(Object.values(spec.prefixes ?? {}))];
  // Builtins and other non-file values (`primitive:cube`, render textures `rt:minimap`).
  if (spec.builtins?.includes(raw) || (/^[a-z][a-z0-9]*:/i.test(raw) && !prefixes.some((p) => raw.startsWith(p)))) {
    return { path: null, builtin: raw, raw };
  }
  let path = raw;
  for (const prefix of prefixes) {
    if (path.startsWith(prefix)) {
      path = path.slice(prefix.length);
      break;
    }
  }
  const hash = path.indexOf('#');
  if (hash >= 0) path = path.slice(0, hash);
  return { path, builtin: null, raw };
}

/** "Material", "Texture", "Mesh"... for the spec (first suffix's name). */
export function assetTypeName(spec: AssetSpec): string {
  if (spec.label) return spec.label;
  const names = [...new Set(suffixList(spec).map((s) => typeName(s)))];
  return names.length === 1 ? names[0]! : names.join(' / ');
}

function typeName(suffix: string): string {
  const s = suffix.toLowerCase();
  return TYPE_NAMES.find(([end]) => s.endsWith(end))?.[1] ?? suffix.replace(/^\./, '').toUpperCase();
}

/** Type name of one file (`Material`, `Texture`, ...), or null for unknown kinds. */
export function fileTypeName(path: string): string | null {
  const lower = path.toLowerCase();
  return TYPE_NAMES.find(([end]) => lower.endsWith(end))?.[1] ?? null;
}

/** `materials/Hero.material.json` → `Hero`; `primitive:cube` → `Cube`. */
export function assetDisplayName(value: string): string {
  if (value.startsWith('primitive:')) {
    const p = value.slice('primitive:'.length);
    return p ? p[0]!.toUpperCase() + p.slice(1) : value;
  }
  const name = value.slice(value.lastIndexOf('/') + 1);
  const compound = name.match(/^(.+?)\.[a-z0-9]+\.json$/i);
  if (compound) return compound[1]!;
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Ranks picker candidates: name matches before path matches; every word must match. */
export function filterAssets(paths: readonly string[], query: string): string[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...paths].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const scored: [string, number][] = [];
  for (const p of paths) {
    const lower = p.toLowerCase();
    const name = assetDisplayName(p).toLowerCase();
    if (!words.every((w) => lower.includes(w))) continue;
    const score = words.reduce((n, w) => n + (name.startsWith(w) ? 0 : name.includes(w) ? 1 : 2), 0);
    scored.push([p, score]);
  }
  return scored.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0], undefined, { numeric: true })).map(([p]) => p);
}

/* ------------------------------------------------------------------ */
/* Drag payload                                                        */

/** dataTransfer type of project files dragged from the Assets panel. */
export const ASSET_MIME = 'application/x-prism-asset';

/**
 * Browsers hide dataTransfer contents until the drop, but drop targets need
 * the path while hovering to accept or refuse; the panel records it here.
 */
let dragging: string | null = null;

export function beginAssetDrag(dataTransfer: DataTransfer, path: string): void {
  dragging = path;
  dataTransfer.setData(ASSET_MIME, path);
  dataTransfer.setData('text/plain', path);
  dataTransfer.effectAllowed = 'copyLink';
}

export function endAssetDrag(): void {
  dragging = null;
}

/** The project file being dragged (during dragover), if the drag came from the Assets panel. */
export function draggedAssetPath(dataTransfer: DataTransfer | null): string | null {
  if (!dataTransfer || !Array.from(dataTransfer.types).includes(ASSET_MIME)) return null;
  return dataTransfer.getData(ASSET_MIME) || dragging;
}
